import { useTranslation } from 'react-i18next';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  ArrowForward,
  DirectionsCar,
  Flight,
  LocalParking,
  Shield,
} from '@mui/icons-material';

import { CarOffer, categoryLabel, isOnTerminal } from '../cars';

interface Props {
  offer: CarOffer;
  /** Retiro y devolución tal como se buscaron ("13/08/2026", "10:00"). */
  pickUp: { date: string; time: string };
  dropOff: { date: string; time: string };
  days: number;
  /** Nombre del punto elegido en el buscador ("Rio de Janeiro–Galeão"). */
  locationName?: string;
}

/**
 * Resumen de lo que se está por reservar. Va maquetado y no como renglones de
 * texto: son cinco datos distintos (vehículo, período, oficinas, acceso a la
 * terminal y cobertura) y en texto corrido no se leen.
 */
export function CarBookingSummary({
  offer,
  pickUp,
  dropOff,
  days,
  locationName,
}: Props) {
  const { t, i18n } = useTranslation();
  const hasTerminalInfo = Boolean(offer.terminalAccess);
  const onTerminal = isOnTerminal(offer);
  const sameOffice = offer.pickUpLocation === offer.dropOffLocation;

  return (
    <Box border={1} borderColor="divider" borderRadius={2} overflow="hidden">
      <Box display="flex" alignItems="center" gap={1.5} px={2} py={1.5}>
        {offer.carImageUrl ? (
          <Box
            component="img"
            src={offer.carImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: 72, height: 48, objectFit: 'contain' }}
          />
        ) : (
          <DirectionsCar color="action" />
        )}
        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle1">{offer.carModel ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[categoryLabel(offer.category, i18n.language), offer.company]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Período: es lo que define el precio, así que va grande y separado. */}
      <Box display="flex" alignItems="center" gap={2} px={2} py={1.5} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('travelServices.cars.pickUp')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {pickUp.date} · {pickUp.time}
          </Typography>
        </Box>
        <ArrowForward fontSize="small" color="disabled" />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('travelServices.cars.dropOff')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {dropOff.date} · {dropOff.time}
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          sx={{ ml: 'auto' }}
          label={t('travelServices.durationDays', { count: days })}
        />
      </Box>

      <Divider />

      <Stack spacing={1} px={2} py={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <LocalParking fontSize="small" color="action" />
          <Typography variant="body2">
            {locationName ? `${locationName} · ` : ''}
            {sameOffice
              ? t('travelServices.cars.sameOffice', { office: offer.pickUpLocation })
              : t('travelServices.cars.twoOffices', {
                pickUp: offer.pickUpLocation,
                dropOff: offer.dropOffLocation,
              })}
          </Typography>
        </Box>

        {hasTerminalInfo && (
          <Box display="flex" alignItems="center" gap={1}>
            <Flight fontSize="small" color={onTerminal ? 'success' : 'action'} />
            <Typography variant="body2" color="text.secondary">
              {t(
                onTerminal
                  ? 'travelServices.cars.onTerminalDetail'
                  : 'travelServices.cars.offTerminalDetail',
              )}
            </Typography>
          </Box>
        )}

        {offer.rateName && (
          <Box display="flex" alignItems="center" gap={1}>
            <Shield fontSize="small" color="action" />
            <Typography variant="body2">{offer.rateName}</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
